import { useTranslation } from 'react-i18next';
import { Trophy } from 'lucide-react';
import { rankProgress, RANK_COLOR, RANK_EMOJI, type Rank } from '../lib/rank';
import { formatInt } from '../lib/format';

interface ReferralStatusProps {
  referralsCount: number;
  /** Cumulative referral earnings, in GRAM. */
  earningsGram: number;
}

/**
 * "Your status" card: the user's referral tier (bronze → diamond), its color,
 * and what's left to reach the next tier. Tiers mirror the recruiter bot's
 * `rank.ts`, so the level shown here matches the AI coach and the Telegram bot.
 */
export function ReferralStatus({ referralsCount, earningsGram }: ReferralStatusProps) {
  const { t } = useTranslation();
  const p = rankProgress(referralsCount, earningsGram);
  const color = RANK_COLOR[p.rank];
  const tierName = (r: Rank) => t(`referrals.tiers.${r}`);

  return (
    <div className="rank-card" style={{ ['--rank-color' as string]: color }}>
      <div className="card-head">
        <Trophy size={16} aria-hidden="true" /> {t('referrals.status.title')}
      </div>
      <div className="rank-card__badge">
        <span className="rank-card__emoji" aria-hidden="true">
          {RANK_EMOJI[p.rank]}
        </span>
        <span className="rank-card__name">{tierName(p.rank)}</span>
      </div>

      {p.next ? (
        <>
          <div className="rank-card__bar" role="presentation">
            <span
              className="rank-card__bar-fill"
              style={{ width: `${Math.round(p.fraction * 100)}%` }}
            />
          </div>
          <p className="rank-card__next">
            {t('referrals.status.toNext', {
              next: tierName(p.next),
              refs: formatInt(p.referralsToNext),
              gram: formatInt(p.earningsToNext),
            })}
          </p>
        </>
      ) : (
        <p className="rank-card__next rank-card__next--max">{t('referrals.status.max')}</p>
      )}
    </div>
  );
}
